import { useEffect, type ComponentProps } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { Card, List, Progress, Segmented, DatePicker, Spin, Typography } from 'antd'
import { ArrowUpOutlined, ArrowDownOutlined, MinusOutlined } from '@ant-design/icons'
import {
  useDashboardStore,
  type PeriodOption,
  type DashboardMetric,
  type ComparisonType,
} from '@/stores/dashboardStore'
import { DashboardChart } from './DashboardChart'
import { formatCurrency, formatCpf } from '@/lib/formatters'
import type { Delta, MesAtualMetric, PendenciaItem, ProximaConcluirItem } from '@/types'

const { Text } = Typography

type DateRangeValue = ComponentProps<typeof DatePicker.RangePicker>['value']

// ─── Entry component: zero logic, reads like wireframe ───────────────────────

export function Dashboard() {
  const fetchDashboard = useDashboardStore((s) => s.fetchDashboard)
  const data = useDashboardStore((s) => s.data)

  useEffect(() => {
    if (!data) fetchDashboard()
  }, [])

  return (
    <div className="pb-8">
      <PageHeader />
      <MesAtualCardsRow />
      <ChartSection />
      <PeriodKpisRow />
      <ActionPanelsRow />
    </div>
  )
}

// ─── PageHeader ───────────────────────────────────────────────────────────────

function PageHeader() {
  return (
    <div className="flex items-center justify-between">
      <Text strong style={{ fontSize: 20 }}>
        Painel
      </Text>
    </div>
  )
}

// ─── MesAtualCardsRow ─────────────────────────────────────────────────────────

function MesAtualCardsRow() {
  const hasMarkupHistory = useDashboardStore((s) => s.data?.hasMarkupHistory)

  return (
    <div className="flex flex-col sm:flex-row flex-wrap gap-4 mt-4!">
      <MatriculasCard />
      {hasMarkupHistory && <ReceitaCard />}
    </div>
  )
}

function MatriculasCard() {
  const metric = useDashboardStore((s) => s.data?.mesAtualCards?.matriculas)
  const loading = useDashboardStore((s) => s.loading)

  return (
    <MesAtualMetricCard
      title="Matrículas este mês"
      metric={metric}
      loading={loading}
      formatValue={(v) => String(Math.round(v))}
    />
  )
}

function ReceitaCard() {
  const metric = useDashboardStore((s) => s.data?.mesAtualCards?.receita)
  const loading = useDashboardStore((s) => s.loading)

  return (
    <MesAtualMetricCard
      title="Receita este mês"
      metric={metric}
      loading={loading}
      formatValue={formatCurrency}
    />
  )
}

function MesAtualMetricCard({
  title,
  metric,
  loading,
  formatValue,
}: {
  title: string
  metric: MesAtualMetric | undefined
  loading: boolean
  formatValue: (v: number) => string
}) {
  if (loading || !metric) {
    return (
      <Card className="flex-1 min-w-[160px]">
        <Spin />
      </Card>
    )
  }

  return (
    <Card className="flex-1 min-w-[160px]">
      <Text type="secondary" style={{ fontSize: 11 }}>
        {title}
      </Text>
      <div className="flex items-baseline gap-2 mt-1">
        <Text strong style={{ fontSize: 22 }}>
          {formatValue(metric.realizado)}
        </Text>
        <InlineDelta delta={metric.deltaRealizado} formatValue={formatValue} />
      </div>
      <div className="mt-2">
        <Text type="secondary" style={{ fontSize: 11 }}>
          Total projetado
        </Text>
        <div className="flex items-baseline gap-2">
          <Text style={{ fontSize: 15 }}>
            {metric.projetado !== null ? formatValue(metric.projetado) : '—'}
          </Text>
          {metric.deltaProjetado && (
            <InlineDelta delta={metric.deltaProjetado} formatValue={formatValue} />
          )}
        </div>
      </div>
    </Card>
  )
}

// ─── InlineDelta ──────────────────────────────────────────────────────────────

function InlineDelta({
  delta,
  formatValue,
}: {
  delta: Delta | null
  formatValue: (v: number) => string
}) {
  if (!delta) return null

  const positive = delta.absolute > 0
  const neutral = delta.absolute === 0

  const icon = neutral ? (
    <MinusOutlined style={{ fontSize: 10 }} />
  ) : positive ? (
    <ArrowUpOutlined style={{ fontSize: 10 }} />
  ) : (
    <ArrowDownOutlined style={{ fontSize: 10 }} />
  )

  const color = neutral ? '#6b7280' : positive ? '#16a34a' : '#dc2626'

  return (
    <span style={{ fontSize: 11, color, whiteSpace: 'nowrap' }}>
      {icon} {formatValue(Math.abs(delta.absolute))}
      {delta.percent !== null && ` (${delta.percent > 0 ? '+' : ''}${delta.percent.toFixed(1)}%)`}
    </span>
  )
}

// ─── DeltaLine (used by period KPI cards) ─────────────────────────────────────

function DeltaLine({
  delta,
  comparisonLabel,
  formatValue = formatCurrency,
}: {
  delta: Delta | null
  comparisonLabel: string
  formatValue?: (v: number) => string
}) {
  if (!delta) return <div className="text-gray-400 text-sm">— {comparisonLabel}</div>

  const positive = delta.absolute > 0
  const neutral = delta.absolute === 0

  const icon = neutral ? (
    <MinusOutlined style={{ color: '#6b7280' }} />
  ) : positive ? (
    <ArrowUpOutlined style={{ color: '#16a34a' }} />
  ) : (
    <ArrowDownOutlined style={{ color: '#dc2626' }} />
  )

  const color = neutral ? '#6b7280' : positive ? '#16a34a' : '#dc2626'

  return (
    <div className="text-sm" style={{ color }}>
      {icon} {formatValue(Math.abs(delta.absolute))}
      {delta.percent !== null &&
        ` (${delta.percent > 0 ? '+' : ''}${delta.percent.toFixed(1)}%)`}{' '}
      {comparisonLabel}
    </div>
  )
}

// ─── ChartSection ─────────────────────────────────────────────────────────────

const PERIOD_OPTIONS: { label: string; value: PeriodOption }[] = [
  { label: 'Este mês', value: 'este-mes' },
  { label: 'Mês passado', value: 'mes-passado' },
  { label: 'Últ. 30 dias', value: 'ultimos-30-dias' },
  { label: 'Personalizado', value: 'personalizado' },
]

const COMPARISON_OPTIONS: { label: string; value: ComparisonType }[] = [
  { label: 'Período anterior', value: 'periodo-anterior' },
  { label: 'Mês anterior', value: 'mes-anterior' },
  { label: 'Ano anterior', value: 'ano-anterior' },
]

function ChartSection() {
  const periodOption = useDashboardStore((s) => s.periodOption)
  const metric = useDashboardStore((s) => s.metric)
  const comparisonType = useDashboardStore((s) => s.comparisonType)
  const hasMarkupHistory = useDashboardStore((s) => s.data?.hasMarkupHistory)
  const setPeriodOption = useDashboardStore((s) => s.setPeriodOption)
  const setCustomRange = useDashboardStore((s) => s.setCustomRange)
  const setComparisonType = useDashboardStore((s) => s.setComparisonType)
  const setMetric = useDashboardStore((s) => s.setMetric)

  function handleRangeChange(value: DateRangeValue) {
    if (!value) {
      setCustomRange(null)
      return
    }
    const [start, end] = value
    if (start && end) {
      setCustomRange([start.format('YYYY-MM-DD'), end.format('YYYY-MM-DD')])
    }
  }

  const metricOptions: { label: string; value: DashboardMetric }[] = hasMarkupHistory
    ? [
        { label: 'Receita', value: 'receita' },
        { label: 'Matrículas', value: 'matriculas' },
      ]
    : [{ label: 'Matrículas', value: 'matriculas' }]

  return (
    <Card className="mt-4!">
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="overflow-x-auto">
          <Segmented
            options={PERIOD_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
            value={periodOption}
            onChange={(v) => setPeriodOption(v as PeriodOption)}
          />
        </div>
        {periodOption === 'personalizado' && (
          <>
            <DatePicker.RangePicker
              format="DD/MM/YYYY"
              onChange={handleRangeChange}
              style={{ maxWidth: '100%' }}
            />
            <Segmented
              options={COMPARISON_OPTIONS.map((o) => ({ label: o.label, value: o.value }))}
              value={comparisonType}
              onChange={(v) => setComparisonType(v as ComparisonType)}
            />
          </>
        )}
        {hasMarkupHistory && (
          <Segmented
            options={metricOptions}
            value={metric}
            onChange={(v) => setMetric(v as DashboardMetric)}
          />
        )}
      </div>
      <DashboardChart />
    </Card>
  )
}

// ─── PeriodKpisRow ────────────────────────────────────────────────────────────

function PeriodKpisRow() {
  const hasMarkupHistory = useDashboardStore((s) => s.data?.hasMarkupHistory)

  return (
    <div
      className={`grid grid-cols-1 gap-4 mt-4! ${hasMarkupHistory ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}
    >
      {hasMarkupHistory && <LucroNoPeriodoCard />}
      <MatriculasNoPeriodoCard />
      <AguardandoPagamentoCard />
    </div>
  )
}

function LucroNoPeriodoCard() {
  const lucro = useDashboardStore((s) => s.data?.periodKpis?.lucro)
  const loading = useDashboardStore((s) => s.loading)

  return (
    <Card title="Lucro no período">
      {loading || !lucro ? (
        <Spin />
      ) : (
        <>
          <Text strong style={{ fontSize: 24 }}>
            {formatCurrency(lucro.value)}
          </Text>
          <DeltaLine delta={lucro.delta} comparisonLabel="vs período anterior" />
        </>
      )}
    </Card>
  )
}

function MatriculasNoPeriodoCard() {
  const matriculas = useDashboardStore((s) => s.data?.periodKpis?.matriculas)
  const loading = useDashboardStore((s) => s.loading)

  return (
    <Card title="Matrículas no período">
      {loading || !matriculas ? (
        <Spin />
      ) : (
        <>
          <Text strong style={{ fontSize: 24 }}>
            {Math.round(matriculas.value)}
          </Text>
          <DeltaLine
            delta={matriculas.delta}
            comparisonLabel="vs período anterior"
            formatValue={(v) => String(Math.round(v))}
          />
        </>
      )}
    </Card>
  )
}

function AguardandoPagamentoCard() {
  const navigate = useNavigate()
  const aguardando = useDashboardStore((s) => s.data?.periodKpis?.aguardando)
  const loading = useDashboardStore((s) => s.loading)

  function navigateToAguardando() {
    navigate('/alunos', { state: { pagamento: 'PENDENTE' } })
  }

  return (
    <Card title="Aguardando pagamento">
      {loading || !aguardando ? (
        <Spin />
      ) : (
        <>
          <Text strong style={{ fontSize: 24 }}>
            {aguardando.count}
          </Text>
          <div className="mt-3">
            <button
              onClick={navigateToAguardando}
              className="text-blue-600 hover:underline text-sm cursor-pointer bg-transparent border-0 p-0"
            >
              Ver alunos aguardando pagamento
            </button>
          </div>
        </>
      )}
    </Card>
  )
}

// ─── ActionPanelsRow ──────────────────────────────────────────────────────────

function ActionPanelsRow() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4!">
      <PendenciasPanel />
      <ProximasConcluirPanel />
    </div>
  )
}

function PendenciasPanel() {
  const pendencias = useDashboardStore((s) => s.data?.pendencias)
  const loading = useDashboardStore((s) => s.loading)

  return (
    <Card title="Pendências">
      {loading ? (
        <Spin />
      ) : (
        <List<PendenciaItem>
          dataSource={pendencias ?? []}
          locale={{ emptyText: 'Nenhuma pendência' }}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Link key="ver" to={`/detalhes/${item.matriculaId}`}>
                  Ver
                </Link>,
              ]}
            >
              <List.Item.Meta
                title={<span className="text-sm">{item.nomeAluno}</span>}
                description={
                  <span className="text-xs">
                    {formatCpf(item.cpf)} · {item.cursoNome} · {item.diasAguardando}d aguardando
                  </span>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Card>
  )
}

function ProximasConcluirPanel() {
  const proximasConcluir = useDashboardStore((s) => s.data?.proximasConcluir)
  const loading = useDashboardStore((s) => s.loading)

  return (
    <Card title="Próximas de concluir">
      {loading ? (
        <Spin />
      ) : (
        <List<ProximaConcluirItem>
          dataSource={proximasConcluir ?? []}
          locale={{ emptyText: 'Nenhum aluno próximo de concluir' }}
          renderItem={(item) => (
            <List.Item
              actions={[
                <Link key="ver" to={`/detalhes/${item.matriculaId}`}>
                  Ver
                </Link>,
              ]}
            >
              <List.Item.Meta
                title={<span className="text-sm">{item.nomeAluno}</span>}
                description={<span className="text-xs">{item.cursoNome}</span>}
              />
              <div style={{ width: 100 }}>
                <Progress percent={Math.round(item.progresso)} size="small" />
              </div>
            </List.Item>
          )}
        />
      )}
    </Card>
  )
}
